import { useLocation, useNavigate } from '@solidjs/router';
import {
  ApiErrorInstance,
  discoverAuthMethodForEmail,
  HOSTED_AUTH_INITIATE_LOGIN_PATH,
  isApi5xxResponseStatus,
  resendPasswordLessEmail,
  withHandleError
} from '~/api';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import {
  AuthMethodData,
  AuthMethodsConnectionTypes,
  PasswordLessAuthTypes
} from '~/api/types/authMethodData.ts';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import logger from '~/utils/logger';
import { ToastContextValue } from '~/components/ui/Toast';
import { usePreviewModeContext } from '~/components/AppData/PreviewModeContext.tsx';
import { CustomizationPageTypes } from '~/pages/Customizations/utils.ts';
import {
  addQueryStringToUri,
  getQueryParam,
  getTranslationKeyFunction
} from '~/utils/commonUtils.ts';
import { AuthRequestStates } from '~/api/types/hostedAuthData.ts';
import { getSlug, HostedAuthPaths } from '~/components/AppRouter/utils.ts';
import { Button } from '~/components/ui/Button';
import {
  LastUsedLoginMethodType,
  updateLastUsedLoginMethod
} from '~/pages/ExternalPages/commons/LastUsedLoginBadge/utils.ts';
import { useHostedAuthStateValidatorContext } from '~/pages/ExternalPages/commons/contexts/HostedAuthStateValidator.tsx';

const TRANSLATION_BASE_PATH = `pages.external.login`;

const LOGIN_OTP_LENGTH = 6;

enum HostedAuthPageTypes {
  LOGIN = 'sign_in',
  SIGNUP = 'sign_up'
}

enum HostedAuthErrorQueryParams {
  ERROR_ID = 'error_id'
}

const PASSWORDLESS_EMAIL_RESEND_CONFIG = {
  initialDelayInSec: 15,
  attemptDelaysInSec: [15, 30]
};

enum LoginBoxSteps {
  SHOW_EMAIL_FORM = 'SHOW_EMAIL_FORM',
  SHOW_OTP_FORM = 'SHOW_OTP_FORM'
}

const getExternalCommonTranslationPath = getTranslationKeyFunction(
  `pages.external.commons`
);

const getTranslationKey = getTranslationKeyFunction(TRANSLATION_BASE_PATH);

const getServerErrorTranslationKey = getTranslationKeyFunction(
  `server_errors.external.login`
);

const getSocialAuthMethods = (
  authMethods: AuthMethodData[]
): AuthMethodData[] =>
  authMethods.filter(
    ({ connectionType }) => connectionType === AuthMethodsConnectionTypes.OAUTH
  );

const getPasskeyAuthMethod = (
  authMethods: AuthMethodData[]
): AuthMethodData[] =>
  authMethods.filter(
    ({ connectionType }) =>
      connectionType === AuthMethodsConnectionTypes.WEBAUTHN
  );

const redirectToLoginInitiateEndpoint = () => {
  window.location.assign(HOSTED_AUTH_INITIATE_LOGIN_PATH);
};

const initiateAuthHandshake = (
  { authInitiationUri }: AuthMethodData,
  intent?: HostedAuthPageTypes
) => {
  authInitiationUri &&
    window.location.assign(
      addQueryStringToUri(authInitiationUri, intent ? `intent=${intent}` : '')
    );
};

const getDummyEmail = () => 'email@yoursaas.com';

const shouldShowEmailExperience = (_: AuthMethodData[]) => {
  // return authMethods.some(
  //   (authMethod) =>
  //     authMethod.connectionType === AuthMethodsConnectionTypes.PASSWORDLESS ||
  //     AuthMethodsConnectionTypes.OIDC ||
  //     AuthMethodsConnectionTypes.SAML
  // );

  // Show email box always for now
  return true;
};

const getPreviewTypeFromQueryParam = () => {
  const previewModeContext = usePreviewModeContext();
  if (!previewModeContext.isPreviewModeEnabled) {
    return null;
  }
  return getQueryParam('previewType');
};

const isLoginPreviewModeEnabled = (
  otpPageType?: CustomizationPageTypes
): boolean => {
  const previewTypeQueryParam = getPreviewTypeFromQueryParam();
  return (
    !!previewTypeQueryParam &&
    ((otpPageType && otpPageType === previewTypeQueryParam) ||
      (!otpPageType &&
        (
          [
            CustomizationPageTypes.LOGIN_BOX,
            CustomizationPageTypes.LOGIN_BOX_WITH_LINK_SUCCESS
          ] as string[]
        ).includes(previewTypeQueryParam)))
  );
};

const isOtpPreviewModeEnabled = (
  otpPageType?: CustomizationPageTypes
): boolean => {
  const previewTypeQueryParam = getPreviewTypeFromQueryParam();
  if (!previewTypeQueryParam) {
    return false;
  }
  if (!otpPageType) {
    const otpPageTypeList = [
      CustomizationPageTypes.LOGIN_BOX_OTP,
      CustomizationPageTypes.LOGIN_BOX_WITH_LINK_OTP
    ] as string[];
    return otpPageTypeList.includes(previewTypeQueryParam);
  }
  return otpPageType === previewTypeQueryParam;
};

const initiateDiscovery = async (
  email: string,
  intent: HostedAuthPageTypes = HostedAuthPageTypes.LOGIN
): Promise<AuthMethodData> => {
  if (!email) {
    throw new Error(AppErrors.NO_EMAIL_PROVIDED_FOR_DISCOVERY);
  }
  try {
    const authMethod = await discoverAuthMethodForEmail({ email, intent });
    if (!authMethod) {
      throw new Error(AppErrors.NO_AUTH_METHOD_DISCOVRED_TO_LOGIN);
    }
    if (authMethod.connectionType !== AuthMethodsConnectionTypes.PASSWORDLESS) {
      updateLastUsedLoginMethod(
        LastUsedLoginMethodType.SSO,
        authMethod.connectionId
      );
      initiateAuthHandshake(authMethod, intent);
    }
    return authMethod;
  } catch (e) {
    throw e;
  }
};

const isPasswordLessLinkAuthMethod = (authMethod: AuthMethodData) =>
  authMethod.connectionType === AuthMethodsConnectionTypes.PASSWORDLESS &&
  authMethod.passwordlessType === PasswordLessAuthTypes.LINK;

const isPasswordLessOtpAuthMethod = (authMethod: AuthMethodData) =>
  authMethod.connectionType === AuthMethodsConnectionTypes.PASSWORDLESS &&
  (authMethod.passwordlessType === PasswordLessAuthTypes.OTP ||
    authMethod.passwordlessType === PasswordLessAuthTypes.LINK_OTP);

const isReauthVerifyState = (): boolean => {
  const { getCurrentAuthState } = useHostedAuthStateValidatorContext();
  const authState = getCurrentAuthState();
  return (
    !!authState &&
    [
      AuthRequestStates.VERIFICATION_MAGIC_LINK_SENT,
      AuthRequestStates.VERIFICATION_MAGIC_LINK_OTP_SENT,
      AuthRequestStates.VERIFICATION_OTP_SENT
    ].includes(authState)
  );
};

const isReauthVerifyMagicLinkOnlyState = (): boolean => {
  const { getCurrentAuthState } = useHostedAuthStateValidatorContext();
  return (
    getCurrentAuthState() === AuthRequestStates.VERIFICATION_MAGIC_LINK_SENT
  );
};

const isReauthVerifyOtpState = (): boolean => {
  const { getCurrentAuthState } = useHostedAuthStateValidatorContext();
  return (
    getCurrentAuthState() === AuthRequestStates.VERIFICATION_OTP_SENT ||
    getCurrentAuthState() === AuthRequestStates.VERIFICATION_MAGIC_LINK_OTP_SENT
  );
};

const isReauthVerifyLinkOtpState = (): boolean => {
  const { getCurrentAuthState } = useHostedAuthStateValidatorContext();
  return (
    getCurrentAuthState() === AuthRequestStates.VERIFICATION_MAGIC_LINK_OTP_SENT
  );
};

const onResendPasswordlessEmail = async ({
  successToastMessage,
  errorToastMessage,
  toaster
}: {
  successToastMessage: string;
  errorToastMessage: string;
  toaster: ToastContextValue;
}): Promise<void> => {
  try {
    await resendPasswordLessEmail();
    toaster.toast().success({
      description: successToastMessage
    });
  } catch (e) {
    logger.error(`Failed to resend email for passwordless method`);
    toaster.toast().error({
      description: errorToastMessage
    });
    // The error needs to be propagated back to the login component
    // so that the retry attempt does not exceed
    throw e;
  }
};

const navigateToHostedAuthRoute = (path: HostedAuthPaths): boolean => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToDiffRoute = !location.pathname.includes(
    getSlug.hostedAuthPages(path, true)
  );
  const searchParams = new URLSearchParams(location.search);
  navigate(
    `${getSlug.hostedAuthPages(path, true)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
  );
  return navigateToDiffRoute;
};

const takeActionBasedOnAuthState = ({
  authState
}: {
  authState: AuthRequestStates;
}): boolean => {
  const location = useLocation();

  let navigateToDiffRoute = false;

  switch (authState) {
    case AuthRequestStates.OTP_VERIFIED:
    case AuthRequestStates.LINK_VERIFIED:
    case AuthRequestStates.SSO_AUTHENTICATED:
      // do nothing
      break;
    case AuthRequestStates.AUTHENTICATION_IN_PROGRESS:
    case AuthRequestStates.OTP_VERIFICATION_PENDING:
    case AuthRequestStates.LINK_SENT_OTP_VERIFICATION_PENDING:
    case AuthRequestStates.MAGIC_LINK_SENT:
      if (
        ![
          getSlug.hostedAuthPages(HostedAuthPaths.LOGIN, true),
          getSlug.hostedAuthPages(HostedAuthPaths.SIGNUP, true)
        ].includes(location.pathname)
      ) {
        navigateToDiffRoute = navigateToHostedAuthRoute(HostedAuthPaths.LOGIN);
      }
      break;
    case AuthRequestStates.ORGANIZATION_SWITCHER:
    case AuthRequestStates.ORGANIZATION_SWITCHER_SIGNUP:
    case AuthRequestStates.ORGANIZATION_SELECTED:
    case AuthRequestStates.ORGANIZATION_SIGNUP:
    case AuthRequestStates.ORG_USER_CREATED:
      navigateToDiffRoute = navigateToHostedAuthRoute(
        HostedAuthPaths.ORG_SWITCHER
      );
      break;
    case AuthRequestStates.VERIFICATION_MAGIC_LINK_SENT:
    case AuthRequestStates.VERIFICATION_MAGIC_LINK_OTP_SENT:
    case AuthRequestStates.VERIFICATION_OTP_SENT:
      navigateToDiffRoute = navigateToHostedAuthRoute(
        HostedAuthPaths.REAUTH_VERIFY
      );
      break;
    case AuthRequestStates.AUTHENTICATION_COMPLETED:
      navigateToDiffRoute = true;
      redirectToLoginInitiateEndpoint();
      break;
    case AuthRequestStates.NO_VALID_AUTH_REQUEST:
    case AuthRequestStates.INVALID_STATE:
    case AuthRequestStates.AUTHENTICATION_FAILED:
    default:
      navigateToDiffRoute = navigateToHostedAuthRoute(HostedAuthPaths.LOGIN);
  }
  return navigateToDiffRoute;
};

const getInvalidAuthRetryMessage = ({
  intl,
  refetchAuthState
}: {
  intl: any;
  refetchAuthState: () => Promise<void>;
}) => {
  return getLocalizedString(`pages.external.errors.inline_auth_timed_out`, {
    intl,
    a: (str: string) => (
      <Button
        type={'button'}
        role={`link`}
        class={`underline text-[1em] font-normal`}
        variant={`link`}
        onClick={async () => {
          await refetchAuthState?.();
          window.location.assign(
            getSlug.hostedAuthPages(HostedAuthPaths.LOGIN, true)
          );
        }}
      >
        {str}
      </Button>
    )
  });
};

const MESSAGE_CODE_TOO_MANY_LOGIN_ATTEMPTS = 'TOO_MANY_LOGIN_ATTEMPTS';
const handleValidateOtpErrors = withHandleError(({ error, intl, params }) => {
  const serverError = error as ApiErrorInstance;
  const errorCode = serverError?.error?.details?.[0]?.error_code;

  if (
    errorCode === AppErrors.INVALID_AUTH_REQUEST &&
    params?.refetchAuthState
  ) {
    return getInvalidAuthRetryMessage({
      intl,
      refetchAuthState: params.refetchAuthState
    });
  }

  const translationBasePath = 'server_errors.login';
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    if (
      errorCode === AppErrors.LOGIN_BOX_INVALID_OTP ||
      errorCode === AppErrors.LINK_EXPIRED
    ) {
      return getLocalizedString(
        `${translationBasePath}.${errorCode === AppErrors.LOGIN_BOX_INVALID_OTP ? 'invalid_otp_code' : 'otp_code_expired'}`,
        {
          intl,
          resend: (str) => (
            <span
              onClick={async () => {
                await params?.resendPasswordLessEmail();
              }}
              class={'underline hover:cursor-pointer'}
            >
              {str}
            </span>
          )
        }
      );
    }
    return getLocalizedString(`${translationBasePath}.invalid_otp`, {
      intl
    });
  } else if (serverError.httpStatus === HttpStatusCode.TooManyRequests) {
    params?.onTooManyAttempt?.();
    return MESSAGE_CODE_TOO_MANY_LOGIN_ATTEMPTS;
  }
});

const handleLoginBoxGenericFailure = withHandleError(
  ({ error, intl, params }) => {
    const serverError = error as ApiErrorInstance;
    const errorCode = serverError?.error?.details?.[0]?.error_code;

    if (
      errorCode === AppErrors.INVALID_AUTH_REQUEST &&
      params?.refetchAuthState
    ) {
      return getInvalidAuthRetryMessage({
        intl,
        refetchAuthState: params.refetchAuthState
      });
    }

    if (
      serverError.httpStatus === HttpStatusCode.BadRequest ||
      isApi5xxResponseStatus(serverError.httpStatus)
    ) {
      if (errorCode === AppErrors.PUBLIC_EMAIL_SIGNUP) {
        return getLocalizedString(
          getServerErrorTranslationKey(`public_email_signup`),
          {
            intl
          }
        );
      } else if (errorCode === AppErrors.DISPOSABLE_EMAIL_SIGNUP) {
        return getLocalizedString(
          getServerErrorTranslationKey(`disposable_email_signup`),
          {
            intl
          }
        );
      }
      return getLocalizedString(
        getServerErrorTranslationKey(`discovery_failed`),
        {
          intl
        }
      );
    }
  }
);

const focusNextPinInputField = (otp: string, ref?: HTMLDivElement) => {
  if (!ref) return;

  const inputWrapper = ref?.querySelector('[data-part="control"]');

  if (!inputWrapper) return;

  const index = Math.min(otp.length, LOGIN_OTP_LENGTH - 1);
  const focusElement = inputWrapper?.children[index];
  (focusElement as HTMLInputElement)?.focus();
};

const handleInvalidOtpInput = (
  value: string,
  setOtpDigits: (value: string[]) => void,
  setFields: (field: string, value: string) => void,
  ref?: HTMLDivElement
) => {
  const sanitizedOtp = value.trim();
  const otpNumericValidator = /^\d*$/; // Numeric or empty string validator

  if (!otpNumericValidator.test(sanitizedOtp)) return;

  setOtpDigits(
    Array(LOGIN_OTP_LENGTH)
      .fill('')
      .map((_, i) => sanitizedOtp[i] || '')
  );
  setFields('otp', sanitizedOtp);

  // Temp fix to focus the next empty pin input field after pasting the user input
  focusNextPinInputField(sanitizedOtp, ref);
};

export {
  LOGIN_OTP_LENGTH,
  HostedAuthPageTypes,
  HostedAuthErrorQueryParams,
  PASSWORDLESS_EMAIL_RESEND_CONFIG,
  MESSAGE_CODE_TOO_MANY_LOGIN_ATTEMPTS,
  LoginBoxSteps,
  getExternalCommonTranslationPath,
  getTranslationKey,
  getServerErrorTranslationKey,
  getSocialAuthMethods,
  getPasskeyAuthMethod,
  redirectToLoginInitiateEndpoint,
  initiateAuthHandshake,
  getDummyEmail,
  shouldShowEmailExperience,
  initiateDiscovery,
  getPreviewTypeFromQueryParam,
  isLoginPreviewModeEnabled,
  isOtpPreviewModeEnabled,
  isPasswordLessLinkAuthMethod,
  isPasswordLessOtpAuthMethod,
  isReauthVerifyLinkOtpState,
  isReauthVerifyMagicLinkOnlyState,
  isReauthVerifyOtpState,
  isReauthVerifyState,
  onResendPasswordlessEmail,
  takeActionBasedOnAuthState,
  handleValidateOtpErrors,
  handleLoginBoxGenericFailure,
  handleInvalidOtpInput,
  getInvalidAuthRetryMessage
};
